import React, { useContext, useState } from 'react';
import { observer } from 'mobx-react-lite';
import SaveBlock from 'widgets/saveBlock';
import { TableLinksHelper } from 'entities/table/model/tableLinksHelper';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import { tablesApi } from 'entities/table/api/tablesApi';
import Modal, { ModalProps } from 'shared/lib/components/modals/modal';
import { useTranslation } from 'react-i18next';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import { AxiosError } from 'axios';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import ErrorModal from 'shared/lib/components/modals/errorModal';
import { Typography } from '@mui/material';
import { UiContext } from 'shared/lib/contexts/uiContext';
import ButtonsContainer from 'shared/lib/components/controls/buttons/buttonsContainer';
import SecondaryButton from 'shared/lib/components/controls/buttons/secondaryButton';
import PrimaryButton from 'shared/lib/components/controls/buttons/primaryButton';
import { useNavigate } from 'react-router-dom';
import { InitialPlacement, PlacementParams } from 'entities/types';

export default observer(function CreateTableController({
  tableHelper,
  handleCancel,
  initialSectionId,
  initialFolderId,
  initialName,
}: {
  tableHelper: TableLinksHelper;
} & InitialPlacement & {
    handleCancel: ModalProps['handleClose'];
  }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mobxStore } = useContext(MobxContext);
  const { getTableSearchParams } = useSearchParamsUtils();
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const [isDublicate, setIsDublicate] = useState(false);
  const [createdParams, setCreatedParams] = useState<
    null | (PlacementParams & { id: string })
  >(null);

  return (
    <>
      <SaveBlock
        onCancel={handleCancel}
        onSubmit={async (params) => {
          try {
            if (params.folderId) {
              const data = await tablesApi.createInFolder({
                name: params.name || '',
                section_id: params.sectionId,
                folder_id: params.folderId,
                links: tableHelper.linksArray,
              });

              setCreatedParams({ ...params, id: data.id });
            } else {
              const data = await tablesApi.createInSection({
                name: params.name || '',
                section_id: params.sectionId,
                folder_id: params.folderId,
                links: tableHelper.linksArray,
              });

              setCreatedParams({ ...params, id: data.id });
            }
          } catch (e) {
            const err = e as AxiosError<{ detail?: string }>;

            console.error(err);

            if (
              err?.response?.status === 400 &&
              err?.response?.data?.detail === BackErrors.TableExists
            ) {
              setIsDublicate(true);
              return;
            }

            mobxStore.notificationsStore.errorMessage = t(
              'notifications.errors.default'
            );
          }

          handleCancel();
        }}
        shouldDropNameAfterSave
        isExternalDisabled={tableHelper.isSomeNotFilled}
        onSubmitValidationError={() => {
          tableHelper.isSubmitError = true;
        }}
        shouldHighlightValidation={tableHelper.isSubmitError}
        // TODO: дубль
        initialSectionId={initialSectionId}
        initialFolderId={initialFolderId}
        initialName={initialName}
        nameParams={{ label: t('formFields.tableName'), initialValue: '' }}
      ></SaveBlock>

      {isDublicate && (
        <ErrorModal
          mainText="Ошибка действия"
          isOpen={true}
          onClose={() => setIsDublicate(false)}
        >
          <Typography variant="f18" sx={{ fontWeight: 300 }}>
            Действие невозможно&nbsp;&mdash; объект с&nbsp;таким названием уже
            существует по&nbsp;указанному адресу
          </Typography>
        </ErrorModal>
      )}

      {createdParams && (
        <Modal
          sx={{ '& .MuiDialog-paper': { gap: sp.s80, pb: sp.s80 } }}
          isOpen={!!createdParams}
          handleClose={() => setCreatedParams(null)}
        >
          <Typography variant="f24">Таблица успешно создана</Typography>

          <ButtonsContainer>
            <SecondaryButton onClick={() => setCreatedParams(null)}>
              Закрыть
            </SecondaryButton>

            <PrimaryButton
              onClick={() => {
                navigate({
                  pathname: '/explorer',
                  search: getTableSearchParams({
                    tableId: createdParams.id,
                  }),
                });
              }}
            >
              Перейти к таблице
            </PrimaryButton>
          </ButtonsContainer>
        </Modal>
      )}
    </>
  );
});
